/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { type ReactNode, useCallback, useEffect } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { type MessageDescriptor, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import PlanIcon from '@atlaskit/icon-lab/core/plan';
import ClipboardIcon from '@atlaskit/icon/core/clipboard';
import EmojiIcon from '@atlaskit/icon/core/emoji';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { KnowledgePillList } from '@atlassian/conversation-assistant-agent/ui/KnowledgePillList';
import { ContentRenderer } from '@atlassian/conversation-assistant-content-renderer/ui/message-renderer/ContentRenderer';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { type useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { ChatGenericError } from '@atlassian/conversation-assistant-ui-components/ui/ChatGenericError';
import { SkillIcon } from '@atlassian/rovo-platform-ui-components/skills/skill-icon';

import { AccordionCard, type AccordionCardItem } from '../../../common/ui/accordion-card';
import { AccordionCardList } from '../../../common/ui/accordion-card-list';
import { ActionCardFooter } from '../../../common/ui/action-card-wrapper';

import { DeleteFieldRenderer } from './delete-field-renderer';
import messages from './messages';
import { ScenarioContentRenderer } from './scenario-content-renderer';
import { SkillList, type Action } from './scenario-content-renderer/skill-list';
import type {
	AgenticSkillNode,
	KnowledgeSource,
	ScenarioToolNode,
	ScenarioUpdateInput,
} from './types';

const styles = cssMap({
	cardContainer: {
		borderRadius: token('radius.xlarge'),
		border: `${token('border.width')} solid ${token('color.border')}`,
	},
});

type FieldRendererProps<T> = {
	fieldValue: T;
	fieldName?: string;
	cloudId?: string;
	productKey: string;
};

const BooleanFieldRenderer = ({ value }: { value: boolean }): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<>{value ? formatMessage(messages.enabledValue) : formatMessage(messages.disabledValue)}</>
	);
};

const AgentKnowledgeSourcesRenderer = ({
	knowledgeSources,
	cloudId,
}: {
	knowledgeSources: KnowledgeSource;
	cloudId: string;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const customEnabled = knowledgeSources.sources?.some((s) => s.enabled) ?? false;
	const allOrgEnabled = knowledgeSources.enabled === false;
	const noOrg = !customEnabled && knowledgeSources.enabled === true;
	return (
		<Stack>
			{allOrgEnabled && formatMessage(messages.allOrganisationalKnowledge)}
			{customEnabled && (
				<KnowledgePillList
					cloudId={cloudId}
					knowledge={[
						{
							enabled: knowledgeSources.enabled,
							sources: knowledgeSources.sources?.map((source) => ({
								enabled: source.enabled,
								source: source.source,
								filters: {
									spaceFilter: source.filters?.confluenceFilter?.spaceFilter?.concat(),
									parentFilter: source.filters?.confluenceFilter?.parentFilter?.concat(),
									projectFilter: source.filters?.jiraFilter?.projectFilter?.concat(),
									jsmProjectFilter: source.filters?.jsmFilter?.jsmProjectFilter?.concat(),
								},
							})),
						},
					]}
				/>
			)}
			{noOrg && formatMessage(messages.noOrganisationalKnowledge)}
		</Stack>
	);
};

const mapFieldsToDisplayInfo: Record<
	string,
	{
		label: MessageDescriptor | (() => MessageDescriptor);
		icon: (label: string) => JSX.Element;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		renderer: (fieldRenderProps: FieldRendererProps<any>) => ReactNode;
	}
> = {
	description: {
		label: messages.descriptionTitle,
		icon: (label: string) => <EmojiIcon label={label} />,
		renderer: (fieldRenderProps: FieldRendererProps<string>) => (
			<>
				{fieldRenderProps.fieldValue === '' ? (
					<DeleteFieldRenderer fieldName={fieldRenderProps.fieldName || ''} />
				) : (
					fieldRenderProps.fieldValue
				)}
			</>
		),
	},
	behavior: {
		label: messages.behaviorTitle,
		icon: (label: string) => <EmojiIcon label={label} />,
		renderer: (fieldRenderProps: FieldRendererProps<string>) => (
			<>
				{fieldRenderProps.fieldValue === '' ? (
					<DeleteFieldRenderer fieldName={fieldRenderProps.fieldName || ''} />
				) : (
					<ContentRenderer
						content={fieldRenderProps.fieldValue}
						productKey={fieldRenderProps.productKey}
						siteId={fieldRenderProps.cloudId ?? ''}
					/>
				)}
			</>
		),
	},
	name: {
		label: messages.nameTitle,
		icon: (label: string) => <EmojiIcon label={label} />,
		renderer: (fieldRenderProps: FieldRendererProps<string>) => <>{fieldRenderProps.fieldValue}</>,
	},
	conversationStarters: {
		label: messages.conversationStartersTitle,
		icon: (label: string) => <EmojiIcon label={label} />,
		renderer: (fieldRenderProps: FieldRendererProps<string[]>) => {
			return (
				<Stack>
					{fieldRenderProps.fieldValue?.map((line, index) => (
						<Box key={index}>{line}</Box>
					))}
				</Stack>
			);
		},
	},
	scenarios: {
		label: () =>
			fg('sa_support_scenario_refresh') ? messages.scenariosTitle : messages.scenariosTitleLegacy,
		icon: (label: string) => <PlanIcon label={label} />,
		renderer: (fieldRenderProps: FieldRendererProps<ScenarioUpdateInput>) => (
			<ScenarioContentRenderer
				scenarioToUpdate={fieldRenderProps.fieldValue}
				cloudId={fieldRenderProps.cloudId || ''}
				productKey={fieldRenderProps.productKey}
			/>
		),
	},
	instructions: {
		label: messages.instructionsTitle,
		icon: (label: string) => <EmojiIcon label={label} />,
		renderer: (fieldRenderProps: FieldRendererProps<string>) => (
			<>
				{fieldRenderProps.fieldValue === '' ? (
					<DeleteFieldRenderer fieldName={fieldRenderProps.fieldName || ''} />
				) : (
					<ContentRenderer
						content={fieldRenderProps.fieldValue}
						productKey={fieldRenderProps.productKey}
						siteId={fieldRenderProps.cloudId ?? ''}
					/>
				)}
			</>
		),
	},
	isDeepResearchEnabled: {
		label: messages.isDeepResearchEnabledTitle,
		icon: (label: string) => <EmojiIcon label={label} />,
		renderer: (fieldRenderProps: FieldRendererProps<boolean>) => (
			<BooleanFieldRenderer value={fieldRenderProps.fieldValue} />
		),
	},
	isWebSearchEnabled: {
		label: messages.isWebSearchEnabledTitle,
		icon: (label: string) => <EmojiIcon label={label} />,
		renderer: (fieldRenderProps: FieldRendererProps<boolean>) => (
			<BooleanFieldRenderer value={fieldRenderProps.fieldValue} />
		),
	},
	knowledgeSources: {
		label: messages.knowledgeSourcesTitle,
		icon: (label: string) => <EmojiIcon label={label} />,
		renderer: (fieldRenderProps: FieldRendererProps<KnowledgeSource>) => (
			<AgentKnowledgeSourcesRenderer
				knowledgeSources={fieldRenderProps.fieldValue}
				cloudId={fieldRenderProps.cloudId ?? ''}
			/>
		),
	},
	tools: {
		label: () =>
			fg('sa_support_scenario_refresh') ? messages.toolsTitle : messages.toolsTitleLegacy,
		icon: (label: string) => <EmojiIcon label={label} />,
		renderer: (fieldRenderProps: FieldRendererProps<ScenarioToolNode[]>) => {
			const actions: Action[] = fieldRenderProps.fieldValue.map((tool) => ({
				actionKey: tool.toolId,
				iconUrl: tool.iconUrl,
				name: tool.displayName,
				tags: tool.tags,
				isActive: true,
			}));
			return <SkillList actions={actions} />;
		},
	},
	agenticSkills: {
		label: messages.agenticSkillsTitle,
		icon: (label: string) => <EmojiIcon label={label} />,
		renderer: (fieldRenderProps: FieldRendererProps<AgenticSkillNode[]>) => {
			const actions: Action[] = fieldRenderProps.fieldValue.map((skill) => ({
				actionKey: skill.skillAri,
				icon: skill.iconKey ? <SkillIcon iconKey={skill.iconKey} /> : undefined,
				name: skill.displayName,
				tags: skill.tags,
				isActive: true,
			}));
			return <SkillList actions={actions} />;
		},
	},
};

const resolveLabel = (label: MessageDescriptor | (() => MessageDescriptor)): MessageDescriptor =>
	typeof label === 'function' ? label() : label;

export const AgentDiffCard = ({
	onConfirm,
	onCancel,
	experienceTracker,
	isDisabled,
	isSubmitting,
	fields,
	cloudId,
	productKey,
}: {
	onConfirm: () => Promise<void>;
	onCancel: () => Promise<void>;
	experienceTracker: ReturnType<typeof useAIMateExperienceTracker>;
	isDisabled: boolean;
	isSubmitting: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	fields: Record<string, any>;
	cloudId: string;
	productKey: string;
}): JSX.Element => {
	di(ErrorBoundary);
	const { formatMessage } = useIntl();

	const accordionCardList: AccordionCardItem[] = Object.keys(fields)
		.filter((key) => {
			const v2OnlyFields = [
				'instructions',
				'isDeepResearchEnabled',
				'isWebSearchEnabled',
				'knowledgeSources',
				'tools',
			];
			const isAgenticSkillsField = key === 'agenticSkills';
			return (
				fields[key] != null &&
				(isAgenticSkillsField
					? fg('sltns-1102-agentic-skills')
					: v2OnlyFields.includes(key)
						? fg('sa_support_scenario_refresh')
						: true)
			);
		})
		.flatMap((fieldKey) => {
			const resolvedLabel = resolveLabel(mapFieldsToDisplayInfo[fieldKey].label);
			if (fieldKey !== 'scenarios') {
				return {
					id: fieldKey,
					title: formatMessage(resolvedLabel),
					icon: mapFieldsToDisplayInfo[fieldKey].icon(formatMessage(resolvedLabel)),
					content: mapFieldsToDisplayInfo[fieldKey].renderer({
						fieldValue: fields[fieldKey],
						fieldName: formatMessage(resolvedLabel),
						cloudId,
						productKey,
					}),
				};
			}
			return fields['scenarios'].map((scenarioToUpdate: ScenarioUpdateInput, index: number) => ({
				id: fieldKey + index,
				title: formatMessage(resolvedLabel),
				subtitle: scenarioToUpdate.scenario.name,
				icon: mapFieldsToDisplayInfo[fieldKey].icon(formatMessage(resolvedLabel)),
				content: mapFieldsToDisplayInfo[fieldKey].renderer({
					fieldValue: scenarioToUpdate,
					cloudId,
					productKey,
				}),
			}));
		});

	const actionCardFooter = (
		<ActionCardFooter testId="agent-diff-card-footer">
			<Inline space="space.150">
				<Button isLoading={isSubmitting} isDisabled={isDisabled} onClick={onCancel}>
					{formatMessage(messages.rejectButton)}
				</Button>

				<Button
					appearance="primary"
					isLoading={isSubmitting}
					isDisabled={isDisabled}
					onClick={onConfirm}
				>
					{accordionCardList.length > 1
						? formatMessage(messages.acceptAllButton)
						: formatMessage(messages.acceptButton)}
				</Button>
			</Inline>
		</ActionCardFooter>
	);

	const hasAccordionCards = accordionCardList.length > 0;

	useEffect(() => {
		if (hasAccordionCards) {
			experienceTracker?.succeed({
				name: ExperienceName.STUDIO_UPDATE_AGENT,
			});
		}
	}, [experienceTracker, hasAccordionCards]);

	const handleRenderError = useCallback(
		(error: Error) => {
			experienceTracker?.fail({
				name: ExperienceName.STUDIO_UPDATE_AGENT,
				error,
			});
		},
		[experienceTracker],
	);

	const cardItem =
		accordionCardList.length === 1
			? accordionCardList[0]
			: {
					id: 'agent-diff-card',
					title: formatMessage(messages.actionCardTitle),
					icon: <ClipboardIcon label={formatMessage(messages.actionCardTitle)} />,
					subtitle: formatMessage(messages.agentPlanCardSubtitle, {
						numberOfChanges: accordionCardList.length,
					}),
					content: <AccordionCardList items={accordionCardList} />,
				};

	return (
		<ErrorBoundary fallback={<ChatGenericError errorType="generic" />} onError={handleRenderError}>
			<div css={styles.cardContainer}>
				<AccordionCard
					item={cardItem}
					isExpanded={true}
					isExpandable={false}
					onToggle={() => {}}
					isFirst={true}
				>
					{actionCardFooter}
				</AccordionCard>
			</div>
		</ErrorBoundary>
	);
};
